import React, { useMemo } from 'react';
import TopicUserRow from './TopicUserRow';
import useAppSelector from '../../app/redux/useAppSelector';
import { gql } from '../../graphql/generated/gql';
import type { DocumentType } from '../../graphql/generated/gql';
import type { UserType } from '../../graphql/generated/graphql';
import { TopicUserRole } from '../../graphql/generated/graphql';

const MEMBERS_FRAGMENT = gql(`
  fragment TopicUserList_members on User {
    ...TopicUserRow_user
  }
`);

type MembersFragment = DocumentType<typeof MEMBERS_FRAGMENT>;

const OWNERS_FRAGMENT = gql(`
  fragment TopicUserList_owners on User {
    ...TopicUserRow_user
  }
`);

type OwnersFragment = DocumentType<typeof OWNERS_FRAGMENT>;

const REQUESTORS_FRAGMENT = gql(`
  fragment TopicUserList_requestors on User {
    ...TopicUserRow_user
  }
`);

type RequestorsFragment = DocumentType<typeof REQUESTORS_FRAGMENT>;

const TOPIC_FRAGMENT = gql(`
  fragment TopicUserList_topic on Topic {
    ...TopicUserRow_topic
  }
`);

type TopicFragment = DocumentType<typeof TOPIC_FRAGMENT>;

const collator = new Intl.Collator();
const compareUsers = (user1: { name: string }, user2: { name: string }) =>
  collator.compare(user1.name, user2.name);

interface TopicUserListProps {
  compact?: boolean;
  members?: MembersFragment[];
  owners?: OwnersFragment[];
  topic?: TopicFragment;
  removeUser?: (user: { id: string; type: UserType }) => unknown;
  requestors?: RequestorsFragment[];
  updateUser?: (
    user: { id: string; type: UserType },
    oldRole: Lowercase<TopicUserRole>,
    newRole: Lowercase<TopicUserRole>,
  ) => unknown;
}

const UPDATE_ROLE_TO = {
  [TopicUserRole.Owner]: TopicUserRole.Member,
  [TopicUserRole.Member]: TopicUserRole.Owner,
  [TopicUserRole.Requestor]: TopicUserRole.Member,
};

const TopicUserList = ({
  compact = false,
  topic,
  members = [],
  owners = [],
  removeUser,
  requestors = [],
  updateUser,
}: TopicUserListProps) => {
  const currentUser = useAppSelector((state) => state.session.user);

  const usersList = useMemo<
    Array<{
      user: MembersFragment | OwnersFragment | RequestorsFragment;
      role: TopicUserRole;
    }>
  >(
    () =>
      [
        ...owners.map((user) => ({ user, role: TopicUserRole.Owner })),
        ...requestors.map((user) => ({ user, role: TopicUserRole.Requestor })),
        ...members.map((user) => ({ user, role: TopicUserRole.Member })),
      ].toSorted((a, b) => {
        if (a.user.id === currentUser.id) {
          return -1;
        }
        if (b.user.id === currentUser.id) {
          return 1;
        }
        return a.role === b.role ? compareUsers(a.user, b.user) : 0;
      }),
    [currentUser.id, members, owners, requestors],
  );

  return (
    <>
      {usersList.map(({ user, role }) => (
        <TopicUserRow
          key={user.id}
          compact={compact}
          topic={topic}
          user={user}
          onRemove={removeUser && (() => removeUser(user))}
          onUpdate={
            updateUser &&
            (() =>
              updateUser(
                user,
                role.toLowerCase() as Lowercase<TopicUserRole>,
                UPDATE_ROLE_TO[role].toLowerCase() as Lowercase<TopicUserRole>,
              ))
          }
          userRole={role}
        />
      ))}
    </>
  );
};

export default TopicUserList;
